import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Tray, PaperPlaneRight, CheckCircle, CaretLeft, Warning } from "@phosphor-icons/react";
import { apiClient } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function SupportInbox({ onClose }) {
  const { t } = useLang();
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiClient.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    apiClient.get("/support/admin/tickets").then(({ data }) => setTickets(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openTicket = async (tk) => {
    setActive(tk);
    const { data } = await apiClient.get(`/support/admin/tickets/${tk.ticket_id}/messages`);
    setMessages(data);
  };

  const sendReply = async () => {
    const msg = reply.trim();
    if (!msg) return;
    setReply("");
    await apiClient.post(`/support/admin/tickets/${active.ticket_id}/reply`, { text: msg });
    setMessages((m) => [...m, { role: "admin", text: msg }]);
  };

  const resolve = async () => {
    await apiClient.patch(`/support/admin/tickets/${active.ticket_id}/resolve`);
    toast.success(t("inbResolved"));
    setActive(null);
    load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="support-inbox">
      <div className="w-full max-w-2xl h-[600px] max-h-[85vh] rounded-2xl border border-white/10 bg-[color:var(--surface)] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-[color:var(--surface-2)]">
          <div className="flex items-center gap-3">
            {active && (
              <button data-testid="inbox-back-btn" onClick={() => { setActive(null); load(); }} className="p-1 rounded hover:bg-white/10">
                <CaretLeft size={18} />
              </button>
            )}
            <Tray size={20} weight="duotone" className="text-[color:var(--accent)]" />
            <h3 className="font-display font-semibold">
              {active ? `${active.user_name} · ${active.user_email}` : t("inbTitle")}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {active && (
              <button
                data-testid="inbox-resolve-btn"
                onClick={resolve}
                className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 inline-flex items-center gap-1.5 transition-colors"
              >
                <CheckCircle size={14} weight="fill" /> {t("inbResolve")}
              </button>
            )}
            <button data-testid="inbox-close-btn" onClick={onClose} className="text-white/50 hover:text-white text-xl px-2">×</button>
          </div>
        </div>

        {!active ? (
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {stats && (
              <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {[
                  { l: t("stUsers"), v: stats.users },
                  { l: t("stGens"), v: Object.values(stats.sounds_by_kind || {}).reduce((a, b) => a + b, 0) + (stats.v2a_jobs?.done || 0) },
                  { l: t("stRevenue"), v: `$${stats.revenue_usd}` },
                  { l: t("stMargin"), v: `$${stats.est_margin_usd}`, sub: `${t("stCost")} ~$${stats.est_api_cost_usd}` },
                ].map((s) => (
                  <div key={s.l} className="rounded-lg border border-white/10 bg-[color:var(--surface-2)] p-3">
                    <p className="text-[9px] font-mono-num uppercase tracking-widest text-white/40">{s.l}</p>
                    <p className="font-display font-bold text-lg">{s.v}</p>
                    {s.sub && <p className="text-[9px] text-white/35">{s.sub}</p>}
                  </div>
                ))}
              </div>
            )}
            {tickets.length === 0 && (
              <p className="text-center text-white/40 text-sm py-16" data-testid="inbox-empty">{t("inbEmpty")}</p>
            )}
            {tickets.map((tk) => (
              <button
                key={tk.ticket_id}
                data-testid="inbox-ticket-row"
                onClick={() => openTicket(tk)}
                className={`w-full text-left rounded-xl border p-4 transition-colors hover:border-white/30 ${
                  tk.unread_by_admin ? "border-[color:var(--accent)]/50 bg-[color:var(--accent)]/5" : "border-white/10 bg-[color:var(--surface-2)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{tk.user_name}</span>
                  <span className="text-xs text-white/40">{tk.user_email}</span>
                  {tk.escalated && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[color:var(--accent)]/20 text-[color:var(--accent)]">
                      <Warning size={11} weight="fill" /> {t("inbEscalated")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/50 mt-1.5 line-clamp-2">{tk.last_message}</p>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-white/5 text-white/90"
                    : m.role === "admin" ? "bg-emerald-500/15 border border-emerald-500/30 text-white/90"
                    : "bg-[color:var(--surface-3)] text-white/60"
                  }`}>
                    <p className="text-[10px] font-mono-num uppercase tracking-widest text-white/35 mb-1">
                      {m.role === "user" ? active.user_name : m.role === "admin" ? t("inbYou") : "AI"}
                    </p>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <input
                data-testid="inbox-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendReply()}
                maxLength={1500}
                placeholder={t("inbReplyPh")}
                className="flex-1 bg-[color:var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/25"
              />
              <button
                data-testid="inbox-reply-send-btn"
                onClick={sendReply}
                disabled={!reply.trim()}
                className="w-11 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] grid place-items-center disabled:opacity-40 transition-colors"
              >
                <PaperPlaneRight size={18} weight="fill" color="white" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
